import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface ErrorInfo {
  title: string;
  message: string | Record<string, any>;
  type: 'error' | 'warning' | 'info';
  details?: string | Record<string, any>;
  timestamp?: string;
}

interface ErrorContextType {
  globalError: ErrorInfo | null;
  setGlobalError: (error: ErrorInfo | null) => void;
  showError: (title: string, message: string | Record<string, any>, details?: string) => void;
  showWarning: (title: string, message: string | Record<string, any>, details?: string) => void;
  showInfo: (title: string, message: string | Record<string, any>, details?: string) => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [globalError, setGlobalError] = useState<ErrorInfo | null>(null);

  const showError = (title: string, message: string | Record<string, any>, details?: string | Record<string, any>) => {
    const formattedMessage = typeof message === 'object' ? JSON.stringify(message) : message;
    const formattedDetails = details && typeof details === 'object' ? JSON.stringify(details) : details;
    
    setGlobalError({
      title,
      message: formattedMessage,
      type: 'error',
      details: formattedDetails,
      timestamp: new Date().toLocaleString()
    });
  };

  const showWarning = (title: string, message: string | Record<string, any>, details?: string | Record<string, any>) => {
    const formattedMessage = typeof message === 'object' ? JSON.stringify(message) : message;
    const formattedDetails = details && typeof details === 'object' ? JSON.stringify(details) : details;
    
    setGlobalError({
      title,
      message: formattedMessage,
      type: 'warning',
      details: formattedDetails,
      timestamp: new Date().toLocaleString()
    });
  };

  const showInfo = (title: string, message: string | Record<string, any>, details?: string | Record<string, any>) => {
    const formattedMessage = typeof message === 'object' ? JSON.stringify(message) : message;
    const formattedDetails = details && typeof details === 'object' ? JSON.stringify(details) : details;
    
    setGlobalError({
      title,
      message: formattedMessage,
      type: 'info',
      details: formattedDetails,
      timestamp: new Date().toLocaleString()
    });
  };

  const clearError = () => {
    setGlobalError(null);
  };

  return (
    <ErrorContext.Provider 
      value={{ 
        globalError, 
        setGlobalError,
        showError,
        showWarning,
        showInfo,
        clearError
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};